// src/components/BankPosting/components/BankPostingPastPostingsList.jsx
// ============================================================================
// BLOCK BP.CMP.005 — Past postings history list
// ----------------------------------------------------------------------------
// PURPOSE
// - Displays historical cash-receipt posting rows returned by /past-postings
// - Keeps display read-only and searchable from the Open Invoices panel
// ============================================================================

import React from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

function formatMoney(value) {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? `$${parsed.toFixed(2)}` : '$0.00';
}

function formatDate(value) {
  if (!value) return '';

  const raw = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [yyyy, mm, dd] = raw.split('-');
    return `${mm}-${dd}-${yyyy}`;
  }

  const parsed = new Date(raw);

  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  const yyyy = parsed.getFullYear();

  return `${mm}-${dd}-${yyyy}`;
}

function trimStr(value) {
  return String(value ?? '').trim();
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function buildDepositKey(row) {
  return [
    trimStr(row?.deposit_date),
    trimStr(row?.bank_code),
    trimStr(row?.deposit_no),
    trimStr(row?.batch_no),
  ].join('|');
}

function sortDepositRows(left, right) {
  if (right.depositTotal !== left.depositTotal) {
    return right.depositTotal - left.depositTotal;
  }

  return right.depositNo.localeCompare(left.depositNo, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

function groupRows(rows) {
  const dateMap = new Map();

  rows.forEach((row, index) => {
    const dateKey = trimStr(row?.deposit_date) || 'Unknown date';

    if (!dateMap.has(dateKey)) {
      dateMap.set(dateKey, {
        date: dateKey,
        deposits: new Map(),
      });
    }

    const dateBucket = dateMap.get(dateKey);
    const depositKey = buildDepositKey(row);
    const depositNo = trimStr(row?.deposit_no);
    const batchNo = trimStr(row?.batch_no);
    const bankCode = trimStr(row?.bank_code);
    const depositDesc = trimStr(row?.deposit_desc);

    if (!dateBucket.deposits.has(depositKey)) {
      dateBucket.deposits.set(depositKey, {
        key: depositKey,
        depositNo,
        batchNo,
        bankCode,
        depositDesc,
        isReversed: Boolean(row?.is_reversed),
        depositComment: trimStr(row?.deposit_comment),
        depositTotal: toNumber(row?.deposit_total ?? row?.posting_total),
        targetTotalMatch: Boolean(row?.target_total_match),
        targetLineAmountMatch: Boolean(row?.target_line_amount_match),
        targetCheckMatch: Boolean(row?.target_check_match),
        invoices: [],
      });
    }

    const deposit = dateBucket.deposits.get(depositKey);
    deposit.isReversed = deposit.isReversed || Boolean(row?.is_reversed);

    const sequenceNo = trimStr(row?.sequence_no);
    const invoiceNo = trimStr(row?.invoice_no);
    const customerNo = trimStr(row?.customer_no);
    const customerName = trimStr(row?.customer_name);
    const checkNo = trimStr(row?.check_no);
    const lineAmount = toNumber(row?.cash_amount_applied);
    const lineComment = trimStr(row?.comment);

    const hasInvoiceNumber = Boolean(invoiceNo);
    const hasAppliedAmount = Math.abs(lineAmount) > 0.0001;
    const shouldIncludeInvoiceLine = hasInvoiceNumber || hasAppliedAmount;

    if (shouldIncludeInvoiceLine) {
      deposit.invoices.push({
        key: `${depositKey}|${invoiceNo}|${customerNo}|${checkNo}|${sequenceNo || index}`,
        invoiceNo,
        customerNo,
        customerName,
        checkNo,
        lineAmount,
        lineComment,
      });
    }
  });

  return Array.from(dateMap.values())
    .map((dateBucket) => ({
      date: dateBucket.date,
      deposits: Array.from(dateBucket.deposits.values())
        .map((deposit) => ({
          ...deposit,
          invoices: [...deposit.invoices].sort((left, right) => {
            const byInvoice = left.invoiceNo.localeCompare(right.invoiceNo, undefined, {
              numeric: true,
              sensitivity: 'base',
            });
            if (byInvoice !== 0) return byInvoice;

            return left.customerNo.localeCompare(right.customerNo, undefined, {
              numeric: true,
              sensitivity: 'base',
            });
          }),
        }))
        .sort(sortDepositRows),
    }))
    .sort((left, right) => right.date.localeCompare(left.date));
}

function buildDepositPrimary(deposit) {
  const depositNo = trimStr(deposit?.depositNo) || '-';
  const batchNo = trimStr(deposit?.batchNo) || '-';
  const depositDesc = trimStr(deposit?.depositDesc);

  return `${formatMoney(deposit?.depositTotal)} - Deposit ${depositNo} - Batch ${batchNo}${depositDesc ? ` - ${depositDesc}` : ''}`;
}

function buildDepositSecondary(deposit) {
  const bankCode = trimStr(deposit?.bankCode) || '-';
  const comment = trimStr(deposit?.depositComment);
  const count = Array.isArray(deposit?.invoices) ? deposit.invoices.length : 0;
  const reversedTag = deposit?.isReversed ? ' | REVERSED' : '';

  return `Bank ${bankCode} | ${count} invoice(s)${reversedTag}${comment ? ` | ${comment}` : ''}`;
}

function buildInvoicePrimary(invoice) {
  const invoiceNo = trimStr(invoice?.invoiceNo) || '-';
  const customerNo = trimStr(invoice?.customerNo) || '-';
  const customerName = trimStr(invoice?.customerName) || '-';

  return `${formatMoney(invoice?.lineAmount)} - ${invoiceNo} - ${customerNo} - ${customerName}`;
}

function buildInvoiceSecondary(invoice) {
  const checkNo = trimStr(invoice?.checkNo) || '-';
  const comment = trimStr(invoice?.lineComment);

  return `Check ${checkNo}${comment ? ` | ${comment}` : ''}`;
}

export default function BankPostingPastPostingsList({
  rows,
  loading,
  hasSearch,
  selectedDepositKey,
  onSelectDeposit,
  minHeight = 190,
  maxHeight = 240,
}) {
  const groupedRows = React.useMemo(() => groupRows(Array.isArray(rows) ? rows : []), [rows]);
  const [expandedDepositKey, setExpandedDepositKey] = React.useState('');

  React.useEffect(() => {
    if (!hasSearch) {
      setExpandedDepositKey('');
      return;
    }

    if (!expandedDepositKey) {
      return;
    }

    const exists = groupedRows.some((dateBucket) =>
      dateBucket.deposits.some((deposit) => deposit.key === expandedDepositKey)
    );

    if (!exists) {
      setExpandedDepositKey('');
    }
  }, [expandedDepositKey, groupedRows, hasSearch]);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        minHeight,
        p: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Past postings
      </Typography>

      {!hasSearch && (
        <Typography variant="caption" color="text.secondary">
          Enter a search to load past postings.
        </Typography>
      )}

      {hasSearch && loading && (
        <Typography variant="caption" color="text.secondary">
          Loading past postings...
        </Typography>
      )}

      {hasSearch && !loading && groupedRows.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          No past postings matched your search.
        </Typography>
      )}

      {hasSearch && groupedRows.length > 0 && (
        <List dense disablePadding sx={{ maxHeight, overflowY: 'auto', overflowX: 'hidden' }}>
          {groupedRows.map((dateBucket) => (
            <Box key={dateBucket.date} sx={{ mb: 1 }}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.25 }}>
                {formatDate(dateBucket.date) || dateBucket.date}
              </Typography>

              {dateBucket.deposits.map((deposit) => {
                const isExpanded = expandedDepositKey === deposit.key;
                const isSelected = selectedDepositKey === deposit.key;

                return (
                  <Box key={deposit.key}>
                    <ListItemButton
                      dense
                      onClick={() => {
                        setExpandedDepositKey((prev) => (prev === deposit.key ? '' : deposit.key));

                        if (onSelectDeposit) {
                          onSelectDeposit(
                            isSelected
                              ? null
                              : {
                                  key: deposit.key,
                                  date: dateBucket.date,
                                  bankCode: deposit.bankCode,
                                  depositNo: deposit.depositNo,
                                  batchNo: deposit.batchNo,
                                  depositDesc: deposit.depositDesc,
                                  isReversed: deposit.isReversed,
                                  depositTotal: deposit.depositTotal,
                                  depositComment: deposit.depositComment,
                                }
                          );
                        }
                      }}
                      sx={{
                        py: 0.25,
                        px: 0.5,
                        bgcolor: isSelected ? 'action.selected' : 'transparent',
                        borderLeft: '3px solid',
                        borderLeftColor:
                          deposit.targetTotalMatch ||
                          deposit.targetLineAmountMatch ||
                          deposit.targetCheckMatch
                            ? 'info.main'
                            : 'transparent',
                      }}
                    >
                      <ListItemText
                        primary={buildDepositPrimary(deposit)}
                        secondary={`${isExpanded ? '▼' : '▶'} ${buildDepositSecondary(deposit)}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItemButton>

                    {isExpanded && (
                      <List dense disablePadding sx={{ pl: 2.5 }}>
                        {deposit.invoices.length > 0 ? (
                          deposit.invoices.map((invoice) => (
                            <ListItem key={invoice.key} dense disableGutters sx={{ py: 0.125 }}>
                              <ListItemText
                                primary={buildInvoicePrimary(invoice)}
                                secondary={buildInvoiceSecondary(invoice)}
                                primaryTypographyProps={{ variant: 'body2' }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                            </ListItem>
                          ))
                        ) : (
                          <ListItem dense disableGutters sx={{ py: 0.125 }}>
                            <ListItemText
                              primary="No invoice detail rows for this deposit."
                              primaryTypographyProps={{
                                variant: 'caption',
                                color: 'text.secondary',
                              }}
                            />
                          </ListItem>
                        )}
                      </List>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </List>
      )}
    </Box>
  );
}

BankPostingPastPostingsList.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      bank_code: PropTypes.string,
      deposit_date: PropTypes.string,
      deposit_no: PropTypes.string,
      batch_no: PropTypes.string,
      deposit_desc: PropTypes.string,
      is_reversed: PropTypes.bool,
      deposit_comment: PropTypes.string,
      deposit_total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      customer_no: PropTypes.string,
      customer_name: PropTypes.string,
      check_no: PropTypes.string,
      invoice_no: PropTypes.string,
      sequence_no: PropTypes.string,
      cash_amount_applied: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      comment: PropTypes.string,
      target_total_match: PropTypes.bool,
      target_line_amount_match: PropTypes.bool,
      target_check_match: PropTypes.bool,
    })
  ).isRequired,
  loading: PropTypes.bool,
  hasSearch: PropTypes.bool,
  selectedDepositKey: PropTypes.string,
  onSelectDeposit: PropTypes.func,
  minHeight: PropTypes.number,
  maxHeight: PropTypes.number,
};

BankPostingPastPostingsList.defaultProps = {
  loading: false,
  hasSearch: false,
  selectedDepositKey: '',
  onSelectDeposit: null,
  minHeight: 190,
  maxHeight: 240,
};
